import {
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import StructureSwatch from '~/components/Structures/StructureSwatch';
import { dicomColorToRgbString } from '~/utils/color';
import { ConstraintToggleRow, ConstraintWarningIcon } from './constraintParts';
import { labelSx, mutedNoteSx } from './constraintStyles';

interface OutsideConstraintSectionProps {
    /** Whether the Outside (collision) constraint is enabled. */
    enabled: boolean;
    /** Structures selectable as protected. */
    candidates: readonly RoiReadModel[];
    /** The active Inside bound — cannot also be a protected structure. */
    activeBoundingRoi: number | null;
    /** roiNumbers currently selected as protected. */
    protectedRoiNumbers: number[];
    /** Whether the set has any approved structure eligible for protection. */
    hasApproved: boolean;
    /** Whether every approved structure is currently protected. */
    allApprovedProtected: boolean;
    /** Whether some (but not all) approved structures are protected. */
    someApprovedProtected: boolean;
    /** Enabled, but none of the selected protections resolve in this set. */
    inactive: boolean;
    /**
     * Erase mode is active: the selection is persisted but not applied to the
     * stroke. Controls stay interactive; a note explains they are dormant now.
     */
    erasing: boolean;
    onToggle: (enabled: boolean) => void;
    onToggleProtected: (roiNumber: number, checked: boolean) => void;
    onToggleProtectAllApproved: (checked: boolean) => void;
}

/**
 * Outside (collision) constraint: subtract a set of protected structures from
 * the stroke so it cannot overwrite a neighbour. Presentational — all state
 * lives in the parent orchestrator.
 */
export default function OutsideConstraintSection({
    enabled,
    candidates,
    activeBoundingRoi,
    protectedRoiNumbers,
    hasApproved,
    allApprovedProtected,
    someApprovedProtected,
    inactive,
    erasing,
    onToggle,
    onToggleProtected,
    onToggleProtectAllApproved,
}: OutsideConstraintSectionProps) {
    return (
        <>
            <ConstraintToggleRow
                labelId="collisionConstraintLabel"
                tooltipId="collisionConstraintTooltip"
                checked={enabled}
                onToggle={onToggle}
            />
            {erasing && (
                <Typography sx={mutedNoteSx}>
                    <FormattedMessage id="constraintNotAppliedWhileErasing" />
                </Typography>
            )}
            {enabled && (
                <>
                    <Box
                        sx={{
                            mt: 0.5,
                            maxHeight: 160,
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {candidates.length === 0 && (
                            <Typography
                                sx={{ ...labelSx, opacity: 0.6, py: 0.5 }}
                            >
                                <FormattedMessage id="collisionNoStructures" />
                            </Typography>
                        )}
                        {candidates.map((roi) => {
                            // Can't protect the structure that's the active
                            // Inside bound (see activeBoundingRoi).
                            const isInsideBound =
                                roi.roiNumber === activeBoundingRoi;
                            return (
                                <FormControlLabel
                                    key={roi.roiNumber}
                                    disabled={isInsideBound}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={
                                                !isInsideBound &&
                                                protectedRoiNumbers.includes(
                                                    roi.roiNumber
                                                )
                                            }
                                            onChange={(_, checked) =>
                                                onToggleProtected(
                                                    roi.roiNumber,
                                                    checked
                                                )
                                            }
                                            sx={{ p: 0.5 }}
                                        />
                                    }
                                    label={
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                opacity: isInsideBound
                                                    ? 0.4
                                                    : 1,
                                            }}
                                        >
                                            <StructureSwatch
                                                color={dicomColorToRgbString(
                                                    roi.color
                                                )}
                                            />
                                            <Typography sx={labelSx}>
                                                {roi.name}
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{ ml: 0, mr: 0 }}
                                />
                            );
                        })}
                    </Box>
                    <Divider
                        sx={{
                            my: 0.5,
                            borderColor: 'rgba(255, 255, 255, 0.12)',
                        }}
                    />
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={allApprovedProtected}
                                    indeterminate={
                                        someApprovedProtected &&
                                        !allApprovedProtected
                                    }
                                    disabled={!hasApproved}
                                    onChange={(_, checked) =>
                                        onToggleProtectAllApproved(checked)
                                    }
                                    sx={{ p: 0.5 }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        ...labelSx,
                                        // With no approved structures the option
                                        // is inapplicable — dim the whole control
                                        // so it reads as prominently disabled
                                        // (rather than flagging it as a warning).
                                        opacity: hasApproved ? 1 : 0.4,
                                    }}
                                >
                                    <FormattedMessage id="collisionProtectAllApproved" />
                                </Typography>
                            }
                            sx={{ ml: 0, mr: 0 }}
                        />
                        {inactive && protectedRoiNumbers.length > 0 && (
                            // Only a genuine problem: the user selected
                            // protections but none resolve in this set (e.g.
                            // deleted / renumbered). An enabled Outside with
                            // nothing selected yet is not flagged.
                            <ConstraintWarningIcon
                                titleId="constraintInactiveOutside"
                                placement="right"
                            />
                        )}
                    </Box>
                </>
            )}
        </>
    );
}
